import { css } from '@emotion/react';
import { Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { darkTheme } from '@/theme/dark-theme';
import { spacing } from '@/theme/tokens';

interface RehearsalSettingsPopoverProps {
  fontSize: number;
  lineWidth: number;
  debugOpen: boolean;
  onFontSizeChange: (value: number) => void;
  onLineWidthChange: (value: number) => void;
  onDebugToggle: () => void;
}

const buttonStyle = css`
  background: none;
  border: none;
  color: ${darkTheme.text};
  cursor: pointer;
  padding: ${spacing.sm};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:hover {
    background: ${darkTheme.sidebarHoverBg};
  }
`;

const popoverStyle = css`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  background: ${darkTheme.surface};
  border: 1px solid ${darkTheme.border};
  border-radius: 8px;
  padding: ${spacing.md};
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

const labelStyle = css`
  color: ${darkTheme.textSecondary};
  font-size: 0.8125rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const valueStyle = css`
  color: ${darkTheme.text};
  font-weight: 500;
`;

const sliderStyle = css`
  width: 100%;
  cursor: pointer;
  accent-color: ${darkTheme.primary};
`;

const toggleRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const toggleStyle = css`
  position: relative;
  width: 36px;
  height: 20px;
  background: ${darkTheme.border};
  border: none;
  border-radius: 10px;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;

  &[aria-checked='true'] {
    background: ${darkTheme.primary};
  }

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
  }

  &[aria-checked='true']::after {
    transform: translateX(16px);
  }
`;

export function RehearsalSettingsPopover({
  fontSize,
  lineWidth,
  debugOpen,
  onFontSizeChange,
  onLineWidthChange,
  onDebugToggle,
}: RehearsalSettingsPopoverProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  return (
    <div
      ref={containerRef}
      css={css`
        position: relative;
      `}
    >
      <button
        type="button"
        aria-label="Settings"
        css={buttonStyle}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Settings size={20} aria-hidden="true" />
      </button>
      {open && (
        <div css={popoverStyle}>
          <div css={sectionStyle}>
            <label css={labelStyle} htmlFor="rehearsal-font-size">
              Font Size <span css={valueStyle}>{fontSize}px</span>
            </label>
            <input
              id="rehearsal-font-size"
              type="range"
              min={24}
              max={96}
              step={1}
              value={fontSize}
              onChange={(e) => onFontSizeChange(Number(e.target.value))}
              css={sliderStyle}
            />
          </div>
          <div css={sectionStyle}>
            <label css={labelStyle} htmlFor="rehearsal-line-width">
              Line Width <span css={valueStyle}>{lineWidth}</span>
            </label>
            <input
              id="rehearsal-line-width"
              type="range"
              min={15}
              max={40}
              step={1}
              value={lineWidth}
              onChange={(e) => onLineWidthChange(Number(e.target.value))}
              css={sliderStyle}
            />
          </div>
          <div css={toggleRowStyle}>
            <span css={labelStyle}>Developer Tools</span>
            <button
              type="button"
              role="checkbox"
              aria-checked={debugOpen}
              aria-label="Developer Tools"
              css={toggleStyle}
              onClick={onDebugToggle}
            />
          </div>
        </div>
      )}
    </div>
  );
}
